/**
 * EmpresaLicitadora.js v4.4.32 — Informe completo de una empresa licitadora.
 *
 * Ruta: /radar-dolor/empresa/:nombre
 *
 * Sustituye al modal de drilldown del RadarDolor. Layout tipo informe
 * consultoría: 2 columnas en desktop, 1 en móvil, sticky header con
 * acciones, sin scroll infinito, URL compartible, base natural para
 * exportar a PDF branded.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/services/api";
import {
  Building2,
  ArrowLeft,
  Globe,
  Linkedin,
  Search,
  FileDown,
  Loader2,
  Trophy,
  Target,
  TrendingDown,
  TrendingUp,
  Euro,
  Calendar,
  Award,
  UserPlus,
  Crosshair,
  Share2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatEUR, perfilComercial } from "@/lib/empresa-licitadora";

export default function EmpresaLicitadora() {
  const { nombre: nombreRaw } = useParams();
  const navigate = useNavigate();
  const empresa = decodeURIComponent(nombreRaw || "");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cargar detalle
  useEffect(() => {
    if (!empresa) return;
    setLoading(true);
    api
      .get(`/empresas-licitadoras/${encodeURIComponent(empresa)}/detalle`)
      .then((resp) => setData(resp.data))
      .catch((error) => {
        console.error(error);
        toast.error("Error al cargar detalle de empresa");
      })
      .finally(() => setLoading(false));
  }, [empresa]);

  const perfil = data ? perfilComercial(data.resumen) : null;
  const nif = data?.derrotas?.[0]?.competidor_info?.nif;

  const copyShareLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success("Enlace copiado al portapapeles");
  };

  if (loading) {
    return (
      <div className="p-12 text-center theme-text-muted min-h-screen flex items-center justify-center">
        <div>
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" />
          <div>Cargando informe de {empresa}...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-12 text-center theme-text-muted min-h-screen flex items-center justify-center">
        <div>
          <div className="mb-3">Sin datos disponibles para esta empresa.</div>
          <Button onClick={() => navigate("/radar-dolor")}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Volver al Radar
          </Button>
        </div>
      </div>
    );
  }

  const { resumen, rivales_recurrentes, sectores_donde_pierde, organos_donde_pierde, derrotas, victorias } = data;

  return (
    <div className="min-h-screen">
      {/* Breadcrumb — oculto en print */}
      <div className="px-6 pt-4 pb-2 print:hidden">
        <Link
          to="/radar-dolor"
          className="inline-flex items-center gap-1 text-sm theme-text-muted hover:theme-text transition"
        >
          <ArrowLeft className="w-4 h-4" /> Radar de Dolor
        </Link>
      </div>

      {/* Header con nombre + perfil + acciones (sticky en pantalla, estático en print) */}
      <div className="sticky top-0 z-10 theme-bg-secondary border-b theme-border px-6 py-4 backdrop-blur print:static print:border-b-2 print:border-black print:pb-3 print:pt-0">
        <div className="flex items-start justify-between gap-4 flex-wrap print:block">
          <div className="flex items-start gap-3 flex-1 min-w-0 print:block">
            <div className="p-2 rounded-lg theme-bg-tertiary flex-shrink-0 print:hidden">
              <Building2 className="w-6 h-6 theme-text" />
            </div>
            <div className="min-w-0 flex-1 print:w-full">
              <div className="flex items-center gap-2 flex-wrap mb-1 print:block">
                <h1 className="text-xl md:text-2xl font-bold theme-text break-words print:text-2xl print:mb-2">
                  {empresa}
                </h1>
                {perfil && (
                  <Badge className={`${perfil.color} border uppercase text-xs font-semibold flex-shrink-0 print:inline-block print:mt-1`}>
                    {perfil.etiqueta}
                  </Badge>
                )}
              </div>
              {nif && (
                <div className="text-xs theme-text-muted font-mono print:mb-2">NIF {nif}</div>
              )}
              {perfil && (
                <p className="text-sm theme-text mt-1.5 print:text-[11pt] print:leading-snug print:mt-2">{perfil.mensaje}</p>
              )}
            </div>
          </div>

          {/* Acciones — ocultas en print (no sirven en papel) */}
          <div className="flex gap-1.5 flex-wrap flex-shrink-0 print:hidden">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                window.open(
                  `https://www.google.com/search?q=${encodeURIComponent(empresa + " contacto comercial")}`,
                  "_blank"
                )
              }
            >
              <Globe className="w-3.5 h-3.5 mr-1.5" /> Google
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                window.open(
                  `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(empresa)}`,
                  "_blank"
                )
              }
              className="text-[#0A66C2]"
            >
              <Linkedin className="w-3.5 h-3.5 mr-1.5" /> LinkedIn
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                window.open(
                  nif
                    ? `https://www.einforma.com/servlet/app/portal/ENTP/cif/${encodeURIComponent(nif)}`
                    : `https://www.einforma.com/buscar-empresa?q=${encodeURIComponent(empresa)}`,
                  "_blank"
                )
              }
            >
              <Search className="w-3.5 h-3.5 mr-1.5" /> eInforma
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={copyShareLink}
              title="Copiar enlace de este informe"
            >
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Compartir
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.print()}
              title="Imprimir / Exportar a PDF"
            >
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Contenido principal: 2 columnas en desktop, 1 en móvil y print */}
      <div className="px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6 print:block print:px-0 print:py-4">
        {/* Columna izquierda: KPIs + análisis agregado */}
        <div className="lg:col-span-1 space-y-4 print:mb-4">
          {/* KPIs principales */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="bg-red-500/10 border border-red-500/40">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 theme-text-muted text-xs uppercase mb-1">
                  <TrendingDown className="w-3.5 h-3.5" /> Derrotas
                </div>
                <div className="text-3xl font-bold text-red-700 dark:text-red-300">
                  {resumen?.total_derrotas || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-500/10 border border-emerald-500/40">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 theme-text-muted text-xs uppercase mb-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Victorias
                </div>
                <div className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                  {resumen?.total_victorias || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="theme-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 theme-text-muted text-xs uppercase mb-1">
                  <Crosshair className="w-3.5 h-3.5" /> Tasa de derrota
                </div>
                <div className={`text-3xl font-bold ${perfil?.colorSolid || "theme-text"}`}>
                  {resumen?.tasa_derrota_pct || 0}%
                </div>
              </CardContent>
            </Card>
            <Card className="theme-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 theme-text-muted text-xs uppercase mb-1">
                  <Euro className="w-3.5 h-3.5" /> Importe perdido
                </div>
                <div className="text-xl font-bold theme-text">
                  {formatEUR(resumen?.importe_total_perdido || 0)}
                </div>
                {resumen?.importe_total_ganado > 0 && (
                  <div className="text-xs theme-text-muted mt-1">
                    vs ganado:{" "}
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {formatEUR(resumen.importe_total_ganado)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Rivales recurrentes */}
          {rivales_recurrentes?.length > 0 && (
            <Card className="theme-card">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                  Rivales que más la han vencido
                </h3>
                <div className="space-y-1.5">
                  {rivales_recurrentes.map((r) => (
                    <div
                      key={r.ganador}
                      className="flex items-center justify-between gap-2 px-3 py-2 theme-bg-tertiary rounded"
                    >
                      <span className="theme-text text-sm truncate flex-1">{r.ganador}</span>
                      <Badge className="bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/40 text-xs flex-shrink-0">
                        {r.veces}x
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sectores donde pierde */}
          {sectores_donde_pierde?.length > 0 && (
            <Card className="theme-card">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-red-600 dark:text-red-400" />
                  Sectores donde pierde
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {sectores_donde_pierde.map((s) => (
                    <Badge
                      key={s.sector}
                      className="theme-bg-tertiary theme-text theme-border border text-xs"
                    >
                      {s.sector} · {s.veces}x
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Órganos donde pierde */}
          {organos_donde_pierde?.length > 0 && (
            <Card className="theme-card">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                  Órganos donde más pierde
                </h3>
                <div className="space-y-1">
                  {organos_donde_pierde.map((o) => (
                    <div
                      key={o.organo}
                      className="flex items-center justify-between text-sm theme-text theme-bg-tertiary px-3 py-1.5 rounded gap-2"
                    >
                      <span className="truncate flex-1">{o.organo}</span>
                      <Badge className="text-xs bg-transparent theme-text-muted theme-border border flex-shrink-0">
                        {o.veces}x
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Columna derecha: timeline de licitaciones */}
        <div className="lg:col-span-2 space-y-4">
          {/* Licitaciones perdidas */}
          {derrotas?.length > 0 && (
            <Card className="theme-card">
              <CardContent className="p-4">
                <h3 className="text-base font-semibold theme-text mb-4 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                  Licitaciones perdidas
                  <Badge className="bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/40 ml-1">
                    {derrotas.length}
                  </Badge>
                </h3>
                <div className="space-y-3">
                  {derrotas.map((d, idx) => {
                    const miPuntos = d.competidor_info?.puntuacion_total;
                    const ganPuntos = d.ganador_puntuacion;
                    const mejorTecnica =
                      typeof miPuntos === "number" &&
                      typeof ganPuntos === "number" &&
                      miPuntos > ganPuntos;

                    return (
                      <div
                        key={idx}
                        className="p-3 theme-bg-tertiary theme-border border rounded-lg"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="theme-text text-sm font-semibold mb-0.5">
                              {d.titulo || d.ref_placsp || "—"}
                            </div>
                            <div className="text-xs theme-text-muted flex items-center gap-2 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {d.organo_contratante || "—"}
                              </span>
                              {d.fecha_adjudicacion && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {d.fecha_adjudicacion}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Euro className="w-3 h-3" />
                                {formatEUR(d.importe)}
                              </span>
                            </div>
                          </div>
                          {mejorTecnica && (
                            <Badge className="bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/40 text-xs flex-shrink-0">
                              <Trophy className="w-3 h-3 mr-1" />
                              Mejor técnica que ganador
                            </Badge>
                          )}
                        </div>

                        {/* Ganador + puntuaciones */}
                        <div className="flex items-center gap-3 text-xs flex-wrap pt-2 border-t theme-border">
                          <div className="flex items-center gap-1.5">
                            <Award className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                            <span className="theme-text-muted">Ganó:</span>
                            <span className="text-amber-700 dark:text-amber-300 font-medium">
                              {d.adjudicatario || "—"}
                            </span>
                          </div>
                          {miPuntos != null && (
                            <div className="theme-text-muted">
                              Tus puntos:{" "}
                              <span className="theme-text font-mono font-semibold">
                                {miPuntos}
                              </span>
                              {ganPuntos != null && (
                                <>
                                  {" "}
                                  vs ganador{" "}
                                  <span className="theme-text font-mono font-semibold">
                                    {ganPuntos}
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          {d.oportunidad_id && (
                            <Link
                              to={`/oportunidades?open=${d.oportunidad_id}`}
                              className="ml-auto text-xs theme-text-muted hover:theme-text underline"
                            >
                              Ver oportunidad →
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Victorias */}
          {victorias?.length > 0 && (
            <Card className="theme-card">
              <CardContent className="p-4">
                <h3 className="text-base font-semibold theme-text mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  Licitaciones ganadas
                  <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 ml-1">
                    {victorias.length}
                  </Badge>
                </h3>
                <div className="space-y-2">
                  {victorias.slice(0, 15).map((v, idx) => (
                    <div
                      key={idx}
                      className="p-2.5 theme-bg-tertiary theme-border border rounded text-xs"
                    >
                      <div className="theme-text text-sm font-medium truncate">
                        {v.titulo || v.ref_placsp || "—"}
                      </div>
                      <div className="theme-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{v.organo_contratante}</span>
                        <span>· {v.fecha_adjudicacion || "—"}</span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-mono">
                          · {formatEUR(v.importe)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {victorias.length > 15 && (
                    <div className="text-xs theme-text-muted text-center py-2">
                      + {victorias.length - 15} victorias más
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
